// Color theme and dark/light theme initializer - runs before React components mount to prevent flashing
export const initializeThemes = () => {
  // Helper function to get system theme preference
  const getSystemTheme = () => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    return 'light'
  }

  // Initialize dark/light theme first
  const initializeLightDarkTheme = () => {
    // Check if there's a saved theme in localStorage
    const savedTheme = localStorage.getItem('theme')
    const theme = savedTheme || getSystemTheme()
    
    // Apply theme to document immediately
    document.documentElement.setAttribute('data-theme', theme)
  }

  // Function to convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null
  }

  // Function to apply color theme
  const applyColorTheme = (colorTheme: { name: string; primary: string; secondary: string }) => {
    const primaryRgb = hexToRgb(colorTheme.primary)
    const secondaryRgb = hexToRgb(colorTheme.secondary)
    
    if (primaryRgb && secondaryRgb) {
      const root = document.documentElement
      
      // Set primary accent color
      root.style.setProperty('--accent-color', colorTheme.primary)
      
      // Set secondary accent color
      root.style.setProperty('--accent-secondary', colorTheme.secondary)
      
      // Set gradient
      root.style.setProperty('--accent-gradient', `linear-gradient(to bottom right, ${colorTheme.primary}, ${colorTheme.secondary})`)
      
      // Set translucent variants
      root.style.setProperty('--accent-translucent', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`)
      root.style.setProperty('--accent-secondary-translucent', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.2)`)
      
      // Set hover and interaction variants
      root.style.setProperty('--accent-hover-background', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`)
      root.style.setProperty('--accent-shadow', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`)
      
      // Set hero backgrounds with accent color hints
      // Dark theme: very dark with slight accent tint
      const darkHeroBg = `rgb(${Math.max(5, Math.floor(primaryRgb.r * 0.06))}, ${Math.max(5, Math.floor(primaryRgb.g * 0.08))}, ${Math.max(15, Math.floor(primaryRgb.b * 0.12))})`
      // Light theme: very light with subtle accent warmth
      const lightHeroBg = `rgb(${Math.min(255, 250 + Math.floor(primaryRgb.r * 0.02))}, ${Math.min(255, 248 + Math.floor(primaryRgb.g * 0.015))}, ${Math.min(255, 245 + Math.floor(primaryRgb.b * 0.01))})`
      
      root.style.setProperty('--hero-bg-dark', darkHeroBg)
      root.style.setProperty('--hero-bg-light', lightHeroBg)
    }
  }

  // Initialize dark/light theme first
  initializeLightDarkTheme()

  // Then initialize color theme
  try {
    const savedColorTheme = localStorage.getItem('color-theme')
    if (savedColorTheme) {
      const colorTheme = JSON.parse(savedColorTheme)
      applyColorTheme(colorTheme)
    }
  } catch (error) {
    console.warn('Failed to initialize saved color theme:', error)
  }
}

// Auto-initialize if we're in a browser environment
if (typeof window !== 'undefined') {
  initializeThemes()
}
